import { Link } from 'react-router-dom'

interface UsageGateProps {
  /** Number of remaining free uses. 0 means limit reached. */
  remaining: number
  /** Monthly limit. */
  limit: number
  /** Feature display name, e.g. "Muhurta lookups". */
  featureName: string
}

/**
 * UsageGate shows a usage counter banner for metered free-tier features.
 * Does not hide content — only displays the remaining-uses indicator.
 * When remaining === 0, shows a hard upgrade prompt instead.
 */
export function UsageGate({ remaining, limit, featureName }: UsageGateProps) {
  const used = limit - remaining

  if (remaining === 0) {
    return (
      <div
        role="alert"
        className="mb-6 flex items-start gap-4 px-4 py-4 rounded-xl border border-inauspicious/30 bg-inauspicious/8"
      >
        <span className="text-inauspicious text-lg leading-none mt-0.5 shrink-0" aria-hidden="true">⚠</span>
        <div className="flex-1 min-w-0">
          <p className="text-star-white text-sm font-medium">Monthly limit reached</p>
          <p className="text-star-soft text-xs mt-0.5">
            You've used all {limit} free {featureName} this month.
            Upgrade to continue with unlimited lookups.
          </p>
        </div>
        <Link
          to="/pricing"
          className="shrink-0 px-4 py-1.5 rounded-lg bg-gradient-to-r from-violet to-violet-mid text-star-white text-xs font-medium hover:shadow-glow-violet transition-all whitespace-nowrap"
        >
          Upgrade →
        </Link>
      </div>
    )
  }

  // Soft warning when on the last use
  if (remaining === 1) {
    return (
      <div className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border border-gold/25 bg-gold/6 text-gold text-sm">
        <span aria-hidden="true">⏱</span>
        <span>
          Last free {featureName.replace(/s$/, '')} this month.{' '}
          <Link to="/pricing" className="underline hover:opacity-80 transition-opacity">
            Upgrade for unlimited →
          </Link>
        </span>
      </div>
    )
  }

  // Normal counter
  return (
    <div
      aria-live="polite"
      className="mb-6 flex items-center gap-3 px-4 py-3 rounded-xl border border-cosmic-mist/40 bg-cosmic-nebula/30 text-star-dim text-xs font-mono"
    >
      <span aria-hidden="true" className="text-base">☽</span>
      <span>
        {remaining} of {limit} free {featureName} remaining this month
        {used > 0 && <> · {used} used</>}.{' '}
        <Link to="/pricing" className="text-gold hover:text-gold-bright transition-colors">
          Upgrade for unlimited →
        </Link>
      </span>
    </div>
  )
}
